import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import { getAuthorizedAxiosClient } from "../../common/axios"
import {
  ACTIVE_ACCOUNT_ID_STORAGE_KEY,
  TOKEN_STORAGE_KEY,
} from "../../common/customerAuthSession"
import { clearSavedViewDrafts } from "../../common/savedViewDrafts"
import SegmentTracker from "../../common/tracking"
import { getProfileFullName, trimLongString } from "../../common/utils"
import { Subscription, isActiveSubscriptionStatus } from "./billing"

function persistActiveAccountId(activeAccount) {
  if (activeAccount?.id) {
    localStorage.setItem(
      ACTIVE_ACCOUNT_ID_STORAGE_KEY,
      String(activeAccount.id)
    )
  } else {
    localStorage.removeItem(ACTIVE_ACCOUNT_ID_STORAGE_KEY)
  }
}

const tokenReceived = createAsyncThunk(
  "user/tokenReceived",
  (token, thunkAPI) => {
    // A magic link logs in as someone else: drop the previous login's account,
    // or its X-Account-Owner-Id is sent with the new token and every request
    // fails "no active access" until a reload.
    if (selectToken(thunkAPI.getState()) !== token) {
      localStorage.removeItem(ACTIVE_ACCOUNT_ID_STORAGE_KEY)
    }
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
    return token
  }
)

/** Login / accept-invite: store token plus parent account id for team members. */
const sessionAccountReceived = createAsyncThunk(
  "user/sessionAccountReceived",
  (payload) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, payload.token)
    persistActiveAccountId(payload.active_account)
    return payload
  }
)

const removeToken = createAsyncThunk(
  "user/removeToken",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState()
    const token = selectToken(state)
    const profile = selectProfile(state)
    try {
      const { data } = await getAuthorizedAxiosClient(token, profile).post(
        "/clients/api/logout/",
        undefined,
        { timeout: 5000 }
      )
      return data || {}
    } catch {
      // Local logout must remain available while the API is unreachable.
      return {}
    } finally {
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      localStorage.removeItem(ACTIVE_ACCOUNT_ID_STORAGE_KEY)
      // Unsaved view edits belong to this login; the next one opens views as saved.
      clearSavedViewDrafts()
    }
  }
)

const retrieveUserProfile = createAsyncThunk(
  "user/retrieveProfile",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState()
    const token = selectToken(state)
    const profile = selectProfile(state)
    try {
      const { data } = await getAuthorizedAxiosClient(token, profile).get(
        "/clients/api/profile/"
      )
      return data
    } catch (error) {
      const status = error?.response?.status
      const detail = String(
        error?.response?.data?.detail || error?.response?.data || ""
      )
      const accessDenied = status === 401 && /no active access/i.test(detail)
      if (accessDenied) {
        localStorage.removeItem(ACTIVE_ACCOUNT_ID_STORAGE_KEY)
        const clearedProfile = {
          ...profile,
          role: undefined,
          active_account: undefined,
          account_owner_name: undefined,
        }
        const { data } = await getAuthorizedAxiosClient(
          token,
          clearedProfile
        ).get("/clients/api/profile/")
        return data
      }
      throw error
    }
  }
)

const switchActiveAccount = createAsyncThunk(
  "user/switchActiveAccount",
  async (accountOwnerId, thunkAPI) => {
    const state = thunkAPI.getState()
    const token = selectToken(state)
    const profile = selectProfile(state)
    try {
      const { data } = await getAuthorizedAxiosClient(token, profile).post(
        "/clients/api/accessible-accounts/switch/",
        { account_owner_id: Number(accountOwnerId) }
      )
      if (!data?.active_account?.id) {
        return thunkAPI.rejectWithValue(
          "Switch response missing active_account"
        )
      }
      // Persist before reload so the next profile fetch keeps the new account.
      persistActiveAccountId(data.active_account)
      return data
    } catch (error) {
      const message =
        error?.response?.data?.account_owner_id ||
        error?.response?.data?.detail ||
        error?.message ||
        "Account switch failed"
      return thunkAPI.rejectWithValue(message)
    }
  }
)

const user = createSlice({
  name: "user",
  initialState: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY)
    return {
      token: token,
      profile: {
        subscription: Subscription.getDefaultSubscription(),
      },
    }
  },
  reducers: {
    updateProfile(state, action) {
      state.profile = action.payload
    },
    clearActiveAccountContext(state) {
      localStorage.removeItem(ACTIVE_ACCOUNT_ID_STORAGE_KEY)
      if (state.profile) {
        state.profile = {
          ...state.profile,
          active_account: undefined,
          role: undefined,
          account_owner_name: undefined,
        }
      }
    },
    decrementFreeCandidatesRemaining(state) {
      if (!isSubscriptionActive(state.profile.subscription)) {
        state.profile.subscription.free_candidates_remaining -= 1
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(tokenReceived.fulfilled, (state, action) => {
      if (state.token !== action.payload) {
        state.profile = { subscription: Subscription.getDefaultSubscription() }
      }
      state.token = action.payload
    })
    builder.addCase(sessionAccountReceived.fulfilled, (state, action) => {
      state.token = action.payload.token
      if (action.payload.active_account) {
        state.profile = {
          ...state.profile,
          active_account: action.payload.active_account,
          role: action.payload.role,
          account_owner_name: action.payload.account_owner_name,
          is_concierge: action.payload.is_concierge,
          is_onebase_staff: action.payload.is_onebase_staff,
        }
      }
    })
    builder.addCase(retrieveUserProfile.fulfilled, (state, action) => {
      state.profile = action.payload
      persistActiveAccountId(action.payload.active_account)
      SegmentTracker.identify(action.payload)
      SegmentTracker.updateProfile(action.payload)
    })
    builder.addCase(switchActiveAccount.fulfilled, (state, action) => {
      state.profile = {
        ...state.profile,
        active_account: action.payload.active_account,
        role: action.payload.role,
        account_owner_name: action.payload.account_owner_name,
        is_concierge: action.payload.is_concierge,
        is_onebase_staff: action.payload.is_onebase_staff,
      }
      persistActiveAccountId(action.payload.active_account)
    })
    builder.addCase(removeToken.fulfilled, (state, action) => {
      state.token = null
    })
  },
})

function isSubscriptionActive(subscription) {
  return isActiveSubscriptionStatus(subscription.status)
}

const selectToken = (state) => state.user.token
const selectProfile = (state) => state.user.profile
const selectProfileLoaded = (state) =>
  Boolean(selectProfile(state).id || selectProfile(state).email)
const selectFullName = (state) => {
  const profile = selectProfile(state)
  return getProfileFullName(profile)
}
const selectSafeLengthFullName = (state) =>
  trimLongString(selectFullName(state), 20)
const selectSubscription = (state) =>
  Subscription.fromAPI(selectProfile(state).subscription) ??
  Subscription.getDefault()
const selectHasSignedUpOnHighRise = (state) =>
  !!selectProfile(state).highrise_customer_id
const selectAccountRole = (state) => selectProfile(state).role || "owner"
const selectActiveAccountId = (state) => {
  const profile = selectProfile(state)
  if (profile?.active_account?.id) {
    return profile.active_account.id
  }
  const stored = localStorage.getItem(ACTIVE_ACCOUNT_ID_STORAGE_KEY)
  return stored ? Number(stored) : null
}
const selectAccountOwnerName = (state) =>
  selectProfile(state).account_owner_name
const selectIsOnebaseStaff = (state) => !!selectProfile(state).is_onebase_staff

export default user.reducer

export const {
  updateProfile,
  decrementFreeCandidatesRemaining,
  clearActiveAccountContext,
} = user.actions

export {
  tokenReceived,
  sessionAccountReceived,
  removeToken,
  retrieveUserProfile,
  switchActiveAccount,
  selectProfile,
  selectProfileLoaded,
  selectToken,
  selectFullName,
  selectSafeLengthFullName,
  selectSubscription,
  selectHasSignedUpOnHighRise,
  selectAccountRole,
  selectActiveAccountId,
  selectAccountOwnerName,
  selectIsOnebaseStaff,
  ACTIVE_ACCOUNT_ID_STORAGE_KEY,
  TOKEN_STORAGE_KEY,
}
